<template>
    <div class="singleModeTicket-controls">
        <div class="ticketPayout">
            <span class="ticketPayout-label">{{ i18n.t('bet_slip_max_payout') }}</span>
            <span class="notranslate ticketPayout-amount">{{ maxPayoutDisplay }}</span>
        </div>
        <TicketVoucherSelect
            v-if="isVoucherRedemptionEnabled"
            v-bind:gaClassPrefix="gaClassPrefix"
            v-bind:options="getTicketVoucherOptions(ticket)"
            v-bind:modelValue="getSelectedVoucherId(ticket.key)"
            v-bind:disabled="isBetSlipInteractionLocked || ticket.isPendingRemoval"
            v-on:update:modelValue="id => setVoucher(ticket.key, id)"
        />
        <StakeBox
            v-else
            v-bind:gaClassPrefix="gaClassPrefix"
            v-bind:disabled="isBetSlipInteractionLocked || ticket.isPendingRemoval"
            v-bind:stake="ticket.stake"
            v-bind:minBet="ticket.minBet"
            v-bind:maxBet="ticket.maxBet"
            v-bind:isInvalid="ticket.stakeError !== null"
            v-bind:isActive="isActive"
            v-on:activate="openStakePad(ticket.key)"
            v-on:updateStake="stake => setStake(ticket.key, stake)"
        />
    </div>
    <TicketMessage v-if="ticketMessage">
        {{ ticketMessage }}
    </TicketMessage>
    <div v-if="!isVoucherRedemptionEnabled && isActive" class="singleModeTicket-expanded">
        <StakePad
            v-bind:gaClassPrefix="gaClassPrefix"
            v-on:appendDigits="digits => appendStakeDigits(ticket.key, digits)"
            v-on:addAmount="amount => addStakeAmount(ticket.key, amount)"
            v-on:max="setMaxStake(ticket.key)"
            v-on:backspace="backspaceStake(ticket.key)"
            v-on:close="closeStakePad"
        />
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed } from 'vue';
    import StakeBox from '@/components/betSlip/Single/StakeBox.vue';
    import StakePad from '@/components/betSlip/Single/StakePad.vue';
    import TicketMessage from '@/components/betSlip/Single/Ticket/TicketMessage.vue';
    import TicketVoucherSelect from '@/components/betSlip/Single/Ticket/TicketVoucherSelect.vue';
    import type { Ticket } from '@/interface/ITicket';
    import { formatSingleModeTicketErrorMessage } from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
    import { i18n } from '@/libraries/i18n';
    import { useAccountStore } from '@/stores/accountStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    const props = withDefaults(defineProps<{
        ticket: Ticket;
        isActive: boolean;
        gaClassPrefix?: string;
    }>(), {
        gaClassPrefix: '',
    });

    const {
        isBetSlipInteractionLocked,
        isVoucherRedemptionEnabled,
    } = storeToRefs(useSingleModeBetSlipStore());
    const {
        addStakeAmount,
        appendStakeDigits,
        backspaceStake,
        closeStakePad,
        openStakePad,
        setMaxStake,
        setStake,
        setVoucher,
        getTicketVoucherOptions,
        getSelectedVoucherId,
        getTicketMaxPayoutValue,
    } = useSingleModeBetSlipStore();
    const { formatMoney, formatMoneyWithSymbolAndDecimal } = useAccountStore();

    const maxPayoutDisplay = computed(() => formatMoneyWithSymbolAndDecimal(getTicketMaxPayoutValue(props.ticket)));

    const ticketMessage = computed(() => {
        const error = props.ticket.openTicketError;
        return error === null ? '' : formatSingleModeTicketErrorMessage(error, formatMoney);
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/singleModeTicketControls";
</style>
